import { useAutocompleteSuggestions } from '@repo/hooks/useAutocompleteSuggestions';
import { useDebounceValue } from '@repo/hooks/useDebounceValue';
import { cn } from '@repo/lib/cn';
import { useMapsLibrary } from '@vis.gl/react-google-maps';
import { PinIcon } from 'lucide-react';
import { useCallback, useState } from 'react';

import { CommandSearchInput } from '@/components/ui/input';

import type { MapAutocompleteProps } from '.';

/**
 * A search input that provides Google Places Autocomplete suggestions and
 * returns a fully-resolved Place object on selection.
 *
 * Internally it:
 * - Uses the Places library to convert a suggestion to a Place.
 * - Calls `fetchFields` to populate commonly used fields (viewport, location, icons).
 * - Resets the autocomplete session after each selection.
 *
 * Usage:
 * @example
 * <MapAutocomplete onPlaceSelect={(place) => {
 *   // e.g. center map or store place
 * }} />
 *
 * @param mapAutocompleteProps - Component props.
 * @returns A SearchInput with autocomplete suggestions.
 */
export function MapAutocomplete(mapAutocompleteProps: MapAutocompleteProps) {
  const { onPlaceSelect, className } = mapAutocompleteProps;
  const places = useMapsLibrary('places');

  const [inputValue, setInputValue] = useState<string>('');
  const debouncedInputValue = useDebounceValue(inputValue, 500);
  const { suggestions, resetSession, isLoading } = useAutocompleteSuggestions(debouncedInputValue);

  const handleSuggestionClick = useCallback(
    async (suggestion: google.maps.places.AutocompleteSuggestion) => {
      if (!places) return;
      if (!suggestion.placePrediction) return;

      const place = suggestion.placePrediction.toPlace();

      await place.fetchFields({
        fields: ['viewport', 'location'],
      });

      setInputValue('');

      // calling fetchFields invalidates the session-token, so we now have to call
      // resetSession() so a new one gets created for further search
      resetSession();

      onPlaceSelect(place);
    },
    [places, onPlaceSelect]
  );

  return (
    <CommandSearchInput
      placeholder="Szukaj miejsca..."
      className={cn('[&>div]:bg-background w-full grow', className)}
      searchQuery={inputValue}
      onSearchQueryChange={setInputValue}
      isLoading={isLoading}
      groups={[
        {
          key: 'places',
          label: 'Miejsca',
          items: suggestions,
          getItemKey: (s) => s.placePrediction?.placeId ?? s.placePrediction?.text.text ?? 'unknown',
          renderItem: (suggestion) => {
            return (
              <div className="flex items-center">
                <PinIcon className="text-muted-foreground size-3.5" />
                <span className="ms-1">{suggestion.placePrediction?.text.text}</span>
              </div>
            );
          },
          onItemSelect: (suggestion) => {
            handleSuggestionClick(suggestion);
          },
        },
      ]}
    />
  );
}
